import { useRef } from "react";

/**
 * `keepPreviousData` for an account-keyed SWR query, only while the data SWR
 * last returned belongs to the SAME account (FE-14).
 *
 * `keepPreviousData: true` (or `Boolean(accountKey)`) made a wallet switch show
 * the previous account's balance, positions and orders until the new account's
 * first response arrived, because SWR serves its laggy copy from any key. With
 * this, the switch renders as loading instead; a refetch for the same account
 * (new API key, polling) still keeps its data on screen.
 */
export const shouldKeepPreviousData = (
  accountKey: string | undefined,
  lastDataAccount: string | undefined
) => Boolean(accountKey) && accountKey === lastDataAccount;

export const useAccountKeepPreviousData = (accountKey: string | undefined) => {
  const lastDataAccount = useRef<string>();
  const keepPreviousData = shouldKeepPreviousData(
    accountKey,
    lastDataAccount.current
  );

  /** Call with the query's `data` right after the SWR hook, every render. */
  const trackData = (data: unknown) => {
    if (accountKey && data !== undefined) lastDataAccount.current = accountKey;
  };

  return { keepPreviousData, trackData };
};
